/**
 * Изгледът на резервациите към касата (docs/edge.md §Резервации без интернет).
 * Касата сервира „Резервации" и „Настани" без интернет САМО от това, което е
 * видяла през огледалото: `GET /reservations/edge-view` (резервациите в
 * прозореца + масите и регионите) и `GET /restaurant-schema` (за рисуването).
 * Екранът на модула чете `GET /reservations?date=`, който НЕ се огледва —
 * затова изгледът се тегли отделно, на 60 s, освен ако касата казва, че има
 * по-пресен от 45 s (`reservations_view_age_ms` в `GET {edge}/sync-state`):
 * при няколко терминала тегли практически само един.
 *
 * Схемата НЕ се тегли на таймер: облакът одитира `GET /restaurant-schema` като
 * „Преглед на Схема на заведението" и всеки терминал би пишел ред в одита на
 * всеки 5 min, без никой да е отварял нищо. Тя стига до касата с подгряването
 * (`edgeWarmup.ts`, веднъж на сесия) и при всяко отваряне на страницата.
 *
 * Тегли се само когато ВСИЧКО е вярно: режим облак, включен резерв, касата
 * умее резервации (`edgeSlice.reservationsOffline`), човекът има
 * `view_reservations` и табът е видим. Самият `PUT {edge}/mirror` го прави
 * response интерсепторът на `api/client.ts` — както за всеки огледван GET.
 * Грешките се гълтат: захранването няма право да пречи на работата.
 */
import { getEdgeSyncState } from "../../api/edge";
import { getReservationsEdgeView } from "../../api/reservations";
import { devLog, getActiveEdgeTicket, getEdgeRuntime } from "./edgeRuntime";
import {
  getEdgeBaseUrl,
  getEdgeConfigState,
  isEdgeLocalMode,
} from "./edgeState";

export const RESERVATIONS_FEED_INTERVAL_MS = 60_000;
/** Изглед, по-млад от това, не се тегли пак (друг терминал го е огледал). */
export const RESERVATIONS_VIEW_FRESH_MS = 45_000;

// Правото на модула (org seed) — BE: reservations/router.py.
const VIEW_RESERVATIONS_PERMISSION = "view_reservations";

let feedTimer: number | null = null;
let tickInFlight = false;
let visibilityListener: (() => void) | null = null;

/** Всички условия за теглене — проверяват се при ВСЕКИ удар. */
export function shouldFeedReservations(): boolean {
  if (isEdgeLocalMode() || !getEdgeConfigState()?.enabled) return false;
  if (!getEdgeBaseUrl() || !getActiveEdgeTicket()) return false;
  // Защитено четене: Redux може да е от по-стар билд (или тестов store) без
  // полето — тогава „касата не умее резервации".
  const state = getEdgeRuntime()?.store.getState();
  if (state?.edge?.reservationsOffline !== true) return false;
  const permissions = state.auth?.permissions ?? [];
  if (!permissions.some((permission) => permission.name === VIEW_RESERVATIONS_PERMISSION)) {
    return false;
  }
  return document.visibilityState === "visible";
}

/** Има ли касата изглед, по-пресен от `RESERVATIONS_VIEW_FRESH_MS`. */
async function tillHasFreshView(base: string): Promise<boolean | null> {
  try {
    const state = await getEdgeSyncState(base, getActiveEdgeTicket());
    const ageMs = state?.reservations_view_age_ms;
    return typeof ageMs === "number" && Number.isFinite(ageMs) && ageMs < RESERVATIONS_VIEW_FRESH_MS;
  } catch (error) {
    // Касата мълчи — огледалото и без това не би стигнало до нея.
    devLog("reservations feed: no sync-state from the till", error);
    return null;
  }
}

/** Един удар на таймера. Изнесен за тестовете. */
export async function runReservationsFeedTick(): Promise<void> {
  if (tickInFlight || !shouldFeedReservations()) return;
  const base = getEdgeBaseUrl();
  if (!base) return;
  tickInFlight = true;
  try {
    const fresh = await tillHasFreshView(base);
    if (fresh === null) return;
    // Режимът може да се е сменил, докато сме питали касата.
    if (!shouldFeedReservations()) return;
    if (fresh) return;
    try {
      await getReservationsEdgeView();
    } catch (error) {
      devLog("reservations feed: edge-view failed", error);
    }
  } finally {
    tickInFlight = false;
  }
}

/** Пуска таймера (идемпотентно) и един удар веднага. */
export function startEdgeReservationsFeed(): void {
  if (feedTimer !== null) return;
  feedTimer = window.setInterval(
    () => void runReservationsFeedTick(),
    RESERVATIONS_FEED_INTERVAL_MS,
  );
  // Таб, върнат на преден план след дълго скрит, не чака цяла минута.
  visibilityListener = () => {
    if (document.visibilityState === "visible") void runReservationsFeedTick();
  };
  document.addEventListener("visibilitychange", visibilityListener);
  void runReservationsFeedTick();
}

export function stopEdgeReservationsFeed(): void {
  if (feedTimer !== null) {
    clearInterval(feedTimer);
    feedTimer = null;
  }
  if (visibilityListener) {
    document.removeEventListener("visibilitychange", visibilityListener);
    visibilityListener = null;
  }
}

/** Само за тестове: забравя таймера. */
export function resetEdgeReservationsFeedForTests(): void {
  stopEdgeReservationsFeed();
  tickInFlight = false;
}
